import React from 'react';
import moment from 'moment';
import { isEmpty, filter, orderBy, trimEnd, has, capitalize } from 'lodash';

import IconButton from '@material-ui/core/IconButton';
import Chip from '@material-ui/core/Chip';
import EmailIcon from '@material-ui/icons/Email';
import NoteIcon from '@material-ui/icons/NoteAdd';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CheckIcon from '@material-ui/icons/Check';
import UpgradeIcon from '@material-ui/icons/ArrowUpward';
import CloseIcon from '@material-ui/icons/Close';
import Modal from '@material-ui/core/Modal';

import Select from 'react-select';
import ACButton from '../../atoms/ACButton';
import ACBookingStatusPill from '../../atoms/ACBookingStatusPill';
import ACDeliveryStatusSelect from '../../atoms/ACDeliveryStatusSelect';

import styles from './BookingSummary.module.scss';

import { ReactComponent as WPLogoActive } from '../../../medias/wp_logo_active.svg';
import { ReactComponent as WPLogoCancelled } from '../../../medias/wp_logo_cancelled.svg';
import { Grid } from '@material-ui/core';
import DoneRoundedIcon from '@material-ui/icons/DoneRounded';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import { getGameplayState, getLatestNotificationProperties } from 'utils/notificationStatus';
import { getBookingEventDate, getHqEventReference, getHqEventUrl } from 'utils/commonFunction';

const formatAnswerForUserResponse = (question) => {
    switch (question?.type) {
        case 'multiple_choice': {
            let sanitizedAnswer;
            if (Array.isArray(question.answer)) {
                sanitizedAnswer = [...question.answer]
            } else if (typeof question.answer === "string") {
                sanitizedAnswer = [question.answer];
            }
            if (sanitizedAnswer) {
                if (question?.configurationData?.options) {
                    return sanitizedAnswer.reduce((acc, answer, index) => {
                        const option = question.configurationData.options.find(({ id }) => (answer === id)) || {};
                        acc += `${index !== 0 ? ', ' : ''}${option.label || answer}`;
                        return acc;
                    }, '');
                } else {
                    return sanitizedAnswer.join(',');
                }
            } else {
                return JSON.stringify(question.answer || '');
            }
        }
        case 'short_text':
        case 'text_area': {
            let sanitizedAnswer;
            if (Array.isArray(question.answer)) {
                sanitizedAnswer = [...question.answer]
            } else if (typeof question.answer === "string") {
                sanitizedAnswer = [question.answer];
            }
            if (sanitizedAnswer) {
                return sanitizedAnswer.join(',');
            } else {
                return JSON.stringify(question.answer || '');
            }
        }
        case 'custom': {
            switch (question?.configurationData?.questionCode) {
                case 'attendee_information': {
                    return question?.answer?.map(attendee => {
                        const formattedAttendeeDetail = [
                            capitalize(attendee?.type), 
                            attendee?.name ? `${capitalize(attendee?.name)} ${attendee?.relation || attendee?.isOrganiser ? `(${capitalize(attendee?.relation?.split('_').join(' ')) || (attendee?.isOrganiser && 'Organiser')})` : ``}` : '', 
                            attendee?.age ? attendee?.age + 'y/o' : '',
                            capitalize((attendee?.dietaryPreference?.split('_').join(' ') || '') + (attendee?.request ? `, ${attendee.request}` : ''))
                         ]?.filter(str => str !== '');
                        return (
                            <>
                                {
                                    (
                                        <p>{formattedAttendeeDetail?.join(' - ')}</p>
                                    )
                                }
                            </>
                        )
                    })
                }
                case 'city_cave': {
                    return (
                        <div>
                        <p>State - {question?.answer?.state}</p>
                        <p>City - {question?.answer?.city}</p>
                        <p>Time - {question?.answer?.timeslot}</p>
                        </div>
                    )
                }
                case 'secret_challenge': 
                case 'weekly_challenge':{
                    return question.answer;
                }
                default: {
                    return JSON.stringify(question.answer || '');
                }
            }
        }
        default: {
            return JSON.stringify(question.answer || '');
        }
    }
}

export default class BookingSummary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            personalisationDetailsModal:{
                visible: false,
                userResponse : null
            }
        };

        this.onChangeBookingManager = this.onChangeBookingManager.bind(this);
        this.onClickBookingManager = this.onClickBookingManager.bind(this);
        this.onChangeDeliveryStatus = this.onChangeDeliveryStatus.bind(this);
    }

    onChangeBookingManager(user) {
        const { booking } = this.props;

        // react-select hands back null when the value is cleared. A component booking is
        // embedded in its occasion and has no _id, so occasionBookingId is what addresses
        // it — undefined on a root booking, which resolves by _id as before.
        if (!user || isEmpty(user)) {
            this.props.onDeleteBookingManagerFunc(booking.id, booking.occasionBookingId);
            return null;
        }

        this.props.onUpdateBookingManagerFunc(booking.id, user.value, booking.occasionBookingId);
    }

    onChangeDeliveryStatus(deliveryStatus) {
        const { booking } = this.props;

        if (!deliveryStatus) return null;
        this.props.onUpdateBookingDeliveryStatusFunc(booking.id, deliveryStatus);
    }

    onClickBookingManager(e) {
        // No need to open the expansion panel if it's just opening the drop down.
        e.stopPropagation();
    }

    getBookingManager() {
        const { users = [], booking } = this.props;
        let userId = -1;
        let selectedUser = {};

        if (booking.userId) {
            userId = booking.userId;
        }

        if (users && !isEmpty(users)) {
            selectedUser = users.find((u) => u.id === userId);
        }

        if (isEmpty(selectedUser)) {
            // null rather than an empty-label option, so react-select shows its placeholder
            // and offers the clear control only where there is an assignment to clear.
            selectedUser = null;
        } else {
            selectedUser = {
                label: selectedUser.name,
                value: selectedUser.id,
            };
        }

        return selectedUser;
    }

    // Shared with the occasion header so the two can never disagree about whether
    // a confirmation / gameplay email has been sent.
    getLatestNotificationProperties(notification, confirmationsOrCodes, booking) {
        return getLatestNotificationProperties(notification, confirmationsOrCodes, booking);
    }

    getHQAlertString(sortedAlerts) {
        let alerts = [];
        const hqDietaryGlutenFreeAlerts = filter(sortedAlerts, (a) => {
            return a.type && a.type === 'hq-dietary-gluten-free';
        });

        if (!isEmpty(hqDietaryGlutenFreeAlerts)) {
            alerts.push(this.getAlertString(hqDietaryGlutenFreeAlerts[0]));
        }

        const hqDietaryVegetarianAlerts = filter(sortedAlerts, (a) => {
            return a.type && a.type === 'hq-dietary-vegetarian';
        });
        if (!isEmpty(hqDietaryVegetarianAlerts)) {
            alerts.push(this.getAlertString(hqDietaryVegetarianAlerts[0]));
        }

        const hqDietaryVeganAlerts = filter(sortedAlerts, (a) => {
            return a.type && a.type === 'hq-dietary-vegan';
        });
        if (!isEmpty(hqDietaryVeganAlerts)) {
            alerts.push(this.getAlertString(hqDietaryVeganAlerts[0]));
        }

        const hqAttendeesAlerts = filter(sortedAlerts, (a) => {
            return a.type && a.type === 'hq-attendees';
        });

        if (!isEmpty(hqAttendeesAlerts)) {
            alerts.push(this.getAlertString(hqAttendeesAlerts[0]));
        }

        const hqProductAlerts = filter(sortedAlerts, (a) => {
            return a.type && a.type === 'hq-product';
        });
        if (!isEmpty(hqProductAlerts)) {
            alerts.push(this.getAlertString(hqProductAlerts[0]));
        }

        const hqCustomerAlerts = filter(sortedAlerts, (a) => {
            return a.type && a.type === 'hq-customer';
        });
        if (!isEmpty(hqCustomerAlerts)) {
            alerts.push(this.getAlertString(hqCustomerAlerts[0]));
        }

        let alertString = trimEnd(alerts.join(''), ', ');

        return !isEmpty(alertString) ? alertString + ' Changed' : '';
    }

    getPromotionOffAlertString(sortedAlerts) {
        let alerts = [];

        const promotionCouponAlerts = filter(sortedAlerts, (a) => {
            return a.type && a.type === 'promotion-coupon';
        });

        if (!isEmpty(promotionCouponAlerts)) {
            alerts.push(this.getAlertString(promotionCouponAlerts[0]));
        }

        let alertString = trimEnd(alerts.join(''), ', ');

        return !isEmpty(alertString) ? alertString + '' : '';
    }

    getRouteChangedString(sortedAlerts) {
        let alerts = [];

        const aexhqRouteAlerts = filter(sortedAlerts, (a) => {
            return a.type && a.type === 'aex-hq-route';
        });
        if (!isEmpty(aexhqRouteAlerts)) {
            alerts.push(this.getAlertString(aexhqRouteAlerts[0]));
        }

        let alertString = trimEnd(alerts.join(''), ', ');

        return !isEmpty(alertString) ? alertString + '' : '';
    }

    extractAlert(alerts = []) {
        let alertString = '';

        const sortedAlerts = orderBy(alerts, ['dateTime'], ['desc']);

        if (!isEmpty(sortedAlerts)) {
            const hqAlertAlert = this.getHQAlertString(sortedAlerts);
            const routeChangedAlert = this.getRouteChangedString(sortedAlerts);

            alertString = !isEmpty(hqAlertAlert) ? hqAlertAlert + ', ' : '';
            alertString += !isEmpty(routeChangedAlert) ? routeChangedAlert + ', ' : '';
        }

        return !isEmpty(alertString) ? trimEnd(alertString, ', ') : '';
    }

    getAlertString(alert) {
        let alertString = '';
        if (alert && !isEmpty(alert)) {
            const { status = '', text } = alert;
            alertString = status === 'active' ? text + ', ' : '';
        }
        return alertString;
    }

    onClickConfirmationNotification = (event) => {
        event.stopPropagation();
        const { booking = {}, getProductByIdFunc, onPreviewConfirmationFunc } = this.props;
        if (booking && booking.productId) getProductByIdFunc(booking.productId);
        onPreviewConfirmationFunc(booking);
    };

    onClickCodeNotification = (event) => {
        event.stopPropagation();
        const { booking = {}, getProductByIdFunc, onPreviewCodeFunc } = this.props;
        if (booking && booking.productId) getProductByIdFunc(booking.productId);
        onPreviewCodeFunc(booking);
    };
    onClickBookingNote = (event) => {
        const { booking = {} } = this.props;
        event.stopPropagation();
        this.props.onClickBookingNote(event, booking);
    }

    onClosePersonalisationDetailsModal = ()=>{
        this.setState({
            personalisationDetailsModal:{
                visible : false,
                userResponse: null
            }
        })
    }

    showPersonalisationDetailsModal = (userResponse)=>{
        this.setState({
            personalisationDetailsModal:{
                visible : true,
                userResponse
            }
        })
    }

    // TODO: Clean it
    renderNotificationIcons(emailStatus, codeStatus, routeId) {
        const { booking = {}, isOccasionBooking } = this.props;
        const { notification, virtualId = '' } = booking;

        // Occasion components are embedded documents, not addressable bookings: the
        // notification and delivery-status endpoints resolve a Mongo _id, and a
        // component's id is a package-component id. Confirmation and gameplay are
        // sent for the whole occasion from its header instead.
        if (isOccasionBooking) {
            return this.renderBookingNoteButton();
        }

        let notifiedColor = 'rgb(102, 205, 170)';
        let notNotifiedColor = 'rgb(211, 47, 47)';
        let queuedColor = '#616161';

        let confirmationStyles = styles.notNotified;
        let confirmationButtonBorder = notNotifiedColor + ' 1px solid';
        let confirmationButtonBackgroundColor = '';
        let confirmationButtonColor = notNotifiedColor;
        let confirmationText = 'Send Confirmation';
        let disableSendConfirmation = false;

        if (emailStatus === true) {
            const lastConfirmationNotification = this.getLatestNotificationProperties(notification, 'confirmations');
            confirmationStyles = styles.notified;
            const status = lastConfirmationNotification.status || '';
            let statusText = status || 'Sent';
            if (status?.toUpperCase() === 'CREATED') {
                statusText = 'In Queue';
            }
            confirmationText = statusText + ' ' + lastConfirmationNotification.date;
            if (lastConfirmationNotification.userGivenName) {
                confirmationText = `${confirmationText} by ${lastConfirmationNotification.userGivenName}`;
            }
            confirmationButtonBackgroundColor = 'transparent';
            confirmationButtonColor = notifiedColor;
            confirmationButtonBorder = notifiedColor + ' 1px solid';
            if (status?.toUpperCase() === 'CREATED') {
                confirmationButtonColor = queuedColor;
                confirmationButtonBorder = queuedColor + ' 1px solid';
                confirmationStyles = styles.queued;
            }            
        }

        let codeStyles = styles.notNotified;
        let codeText = 'Send Clues';
        let disableSendCode = false;
        let codeButtonBorder = notNotifiedColor + ' 1px solid';
        let codeButtonBackgroundColor = '';
        let codeButtonColor = notNotifiedColor;

        if (codeStatus === true) {
            const lastCodesNotification = this.getLatestNotificationProperties(notification, 'codes', booking);
            codeStyles = styles.notified;
            const status = booking.deliveryQueueStatus;
            let statusText = "";
            console.log({status})
            if (status === 'CREATED') {
                statusText = 'In Queue';
            } else if (status === 'SENT') {
                statusText = 'Sent';
            }
            codeText = 'Clues ' + statusText + ' ' + lastCodesNotification.date;
            if (lastCodesNotification.userGivenName) {
                codeText = `${codeText} by ${lastCodesNotification.userGivenName}`;
            }
            codeButtonBackgroundColor = 'transparent';
            codeButtonColor = notifiedColor;
            codeButtonBorder = notifiedColor + ' 1px solid';
            if (status === 'CREATED') {
                codeButtonColor = queuedColor;
                codeButtonBorder = queuedColor + ' 1px solid';
                codeStyles = styles.queued;
            }    
        }

        return (
            <React.Fragment>
                <ACButton
                    text={confirmationText}
                    iconLast={false}
                    onClickFunc={this.onClickConfirmationNotification.bind(this)}
                    border={confirmationButtonBorder}
                    backgroundColor={confirmationButtonBackgroundColor}
                    color={confirmationButtonColor}
                    margin="5px 5px 5px 5px"
                    padding="5px 15px"
                    disabled={disableSendConfirmation}
                    icon={<EmailIcon className={confirmationStyles} key="email-icon" />}
                    className={styles.sendNotificationButtonStyle}
                />
                {(routeId && !isEmpty(routeId)) || (virtualId && !isEmpty(virtualId)) ? (
                    <ACButton
                        text={codeText}
                        iconLast={false}
                        onClickFunc={this.onClickCodeNotification.bind(this)}
                        border={codeButtonBorder}
                        backgroundColor={codeButtonBackgroundColor}
                        color={codeButtonColor}
                        margin="5px 5px 5px 5px"
                        padding="5px 15px"
                        disabled={disableSendCode}
                        width="100px"
                        icon={<EmailIcon className={codeStyles} key="email-icon" />}
                        className={styles.sendNotificationButtonStyle}
                    />
                ) : null}
                {this.renderBookingNoteButton()}
            </React.Fragment>
        );
    }

    renderBookingNoteButton() {
        return (
            <ACButton
                text="Booking note"
                iconLast={false}
                onClickFunc={this.onClickBookingNote.bind(this)}
                border="1px solid"
                margin="5px 5px 5px 5px"
                padding="5px 15px"
                icon={<NoteIcon key="note-icon" />}
                className={styles.sendNotificationButtonStyle}
            />
        );
    }

    renderUsers() {
        const { users = [] } = this.props;
        let selectedUser = this.getBookingManager();

        let mUsers = [];

        if (!users) return null;

        users.forEach((user, index) => {
            let mUser = {
                label: user.name,
                value: user.id,
            };
            mUsers.push(mUser);
        });

        return (
            <div className={styles.selectInputContainer}>
                <span className={styles.boldFont}>Delivery Manager</span>
                <Select
                    className={styles.usersSelect}
                    TextFieldProps={{
                        label: 'Provider Name 2',
                        InputLabelProps: {
                            shrink: true,
                        },
                        placeholder: 'Select a user',
                    }}
                    options={mUsers}
                    value={selectedUser}
                    onChange={this.onChangeBookingManager}
                    isClearable
                />
            </div>
        );
    }

    renderDeliveryStatus() {
        const { booking, isOccasionBooking } = this.props;

        // Delivery status is an occasion-level concern. A component booking is an
        // embedded document whose id is `<hqId>-<componentId>`, but the delivery-status
        // endpoint resolves a Mongo _id and pushes to `booking.hq.id` — and every
        // component inherits the occasion's hq, so N components would fight over one HQ
        // event's status. The occasion header carries the control instead.
        if (isOccasionBooking) return null;

        if (getGameplayState(booking) === 'none') return null;

        return (
            <div className={styles.deliveryStatusContainer}>
                <ACDeliveryStatusSelect
                    value={booking.deliveryStatus}
                    onChangeFunc={this.onChangeDeliveryStatus}
                />
            </div>
        );
    }

    renderRouteIcon(routeId) {
        if (routeId) return null;
        return <IconButton Icon={AddCircleOutlineIcon} text="Set Route" />;
    }

    renderStatusButton(booking) {
        const { isOccasionBooking } = this.props;

        // HQ status belongs to the occasion, not its parts. A component's `hq` is a
        // snapshot copied in when the component was built, and HQ status updates only
        // write the root `hq.status` — so a component's copy is frozen at whatever the
        // event was when it was created. The occasion header shows the live one.
        if (isOccasionBooking) return null;

        return <ACBookingStatusPill hq={booking.hq || {}} />;
    }

    renderAlert(alert) {
        return alert ? <div className={styles.alert} dangerouslySetInnerHTML={{ __html: alert || '' }} /> : null;
    }

    renderHasAddOns(hasAddOns) {
        if (!hasAddOns) return null;

        return (
            <div style={{ alignItems: 'center', color: 'red', display: 'flex' }}>
                <CheckIcon style={{ marginRight: '5px' }} /> AddOns
            </div>
        );
    }

    // An upgrade is an add-on as far as HQ is concerned — it arrives inside the same
    // `extras.addOns` string, named `Upgrade – <tier>`. aex derives `hasUpgrade` from that
    // (`booking/component-booking.ts` isUpgradeItem) so ops can tell a tier upgrade from a
    // bought extra without reading the Inclusion column.
    renderHasUpgrade(hasUpgrade) {
        if (!hasUpgrade) return null;

        return (
            <div style={{ alignItems: 'center', color: '#7b1fa2', display: 'flex' }}>
                <UpgradeIcon style={{ marginRight: '5px' }} /> Upgrade
            </div>
        );
    }
    

    /**
     * What is genuinely this row's own, at the foot of whichever column comes first. On a legacy
     * booking that is the HQ-reference column; on an occasion component the reference and the date
     * are gone (the occasion header owns them, since every component inherits its `hq`), so these
     * sit under the component name instead.
     */
    renderRowFlags(promotionAlert, hasAddOns, hasUpgrade) {
        return (
            <React.Fragment>
                <span style={{ color: 'red' }}>{promotionAlert}</span>
                {this.renderHasAddOns(hasAddOns)}
                {this.renderHasUpgrade(hasUpgrade)}
            </React.Fragment>
        );
    }

    renderPersonalisationDetails=(userResponse)=>{
        return(
            <>
            {userResponse?.sections?.map(section => {
                return (
                    <Grid item xs={12} key={section?.id}>
                        <Grid container>
                            {section?.questions?.map((question) => {
                                return (
                                    <Grid item xs={12} key={question?.id}>
                                        <p style={{fontWeight:'bold'}}>
                                            {question?.presentationData?.title}
                                        </p>
                                        <div>
                                            {formatAnswerForUserResponse(question)}
                                        </div>
                                    </Grid>
                                )
                            })}
                        </Grid>
                    </Grid>
                )
            })}
            {!userResponse && (
                <p>No details found</p>
            )}
            </>
        )
    }

    render() {
        const { booking, isOccasionBooking } = this.props;
        const notification = booking.notification || {};
        const hq = booking.hq || {};
        const routeId = booking.routeId || '';
        const customer = hq.customer || {};
        const alerts = booking.alerts || [];
        const promotionAlert = this.getPromotionOffAlertString(alerts);

        const product = booking.product || {};
        const { productCities } = product;
        let productLocation = '';

        if (product.category === 'digital') {
            productLocation = 'digital';
        } else if (productCities && productCities.length === 1 && productCities[0].city) {
            productLocation = productCities[0].city;
        }

        const phoneNumber = customer.phone ? customer.phone.number : '';

        // Shared with the occasion header, which is where an occasion's copy of these is now
        // rendered. The two blocks this replaced computed the same date twice, the second
        // overwriting the first.
        const { dateText, timeText } = getBookingEventDate(booking);
        const hqLink = getHqEventUrl(booking);
        const emailStatus = !isEmpty(this.getLatestNotificationProperties(notification, 'confirmations'));
        const codeStatus = !isEmpty(this.getLatestNotificationProperties(notification, 'codes', booking));

        let glutenFree = '';
        let vegetarian = '';
        let vegan = '';
        let requirementNotes = [];
        let addOns = [];
        let hasAddOns = false;
        let hasUpgrade = false;
        let hasDietaryResponse = false;
        const { experiencePersonalisationDetails: userExperienceResponse } = booking;
        const attendeeInformationResponse = userExperienceResponse?.userResponse?.sections?.[0]?.questions?.find(question => question?.configurationData?.questionCode === "attendee_information");
        const cityCaveResponse = userExperienceResponse?.userResponse?.sections?.[0]?.questions?.find(question => question?.configurationData?.questionCode === "city_cave");

        if (hq.dietaries) {
            const dietaries = hq.dietaries;
            glutenFree = dietaries.glutenFree === true ? 'GF' : '';
            vegetarian = dietaries.vegetarian === true ? 'Vegetarian' : '';
            vegan = dietaries.vegan === true ? 'Vegan' : '';
            // TODO: Delete this
            if (dietaries.allergies) requirementNotes.push(<p className={styles.noMargin}>{dietaries.allergies}</p>);
            if (!dietaries.allergies && dietaries.allergy) requirementNotes.push(<p className={styles.noMargin}>{dietaries.allergy}</p>);
        }
        if(cityCaveResponse?.answer) {
            const formattedCityCaveResponse = [cityCaveResponse?.answer?.state, cityCaveResponse?.answer?.city, cityCaveResponse?.answer?.timeslot]
            .filter(Boolean)
            .join(", ");
            if(formattedCityCaveResponse) requirementNotes.push(<p className={styles.noMargin}>{formattedCityCaveResponse}</p>);
        }
        hasDietaryResponse = attendeeInformationResponse?.answer?.some(item => item && 'dietaryPreference' in item);
        if(hasDietaryResponse) {
                const preferenceLabels = {
                    VEGAN: "Vegan",
                    VEGETARIAN: "Vegetarian",
                    GLUTEN_FREE: "Gluten Free",
                    };
                const counts = {};

                for (const item of attendeeInformationResponse?.answer) {
                const { dietaryPreference, type } = item;
                if(dietaryPreference) {
                    const key = `${dietaryPreference}_${type}`;
                    counts[key] = (counts[key] || 0) + 1;
                }
                }


                const result = Object.entries(counts).map(([key, count]) => {
                    const lastUnderscore = key.lastIndexOf('_');
                    const diet = key.substring(0, lastUnderscore);
                    const type = key.substring(lastUnderscore + 1);
                    const label = preferenceLabels[diet] || capitalize(diet.toLowerCase().replace(/_/g, ' '));
                    return `${label} X ${count} (${type})`;
                });
                addOns = result.map( diet => (
                    <p style={{
                        margin: 0
                    }}>{diet}</p>
                ))
        } 
        
        if (hq.extras && !isEmpty(hq.extras)) {
            const { extras } = hq;
            hasAddOns = extras.hasAddOns;
            hasUpgrade = extras.hasUpgrade;
            if (extras?.addOns && !isEmpty(extras.addOns)) {
                addOns.push(
                    <p style={{ margin: 0 }} key="addOns">
                    {extras.addOns}
                    </p>
                );
            }
        }
        const alertMessageFromArray = this.extractAlert(alerts);

        // TODO: Legacy variable 'alert'. Remove later if not needed
        const { alert = '' } = booking;
        let alertMessage = !isEmpty(alerts) ? alertMessageFromArray : alert; // this.extractAlert(alerts);

        if (booking.hasError) {
            console.log('corruptStopsError', this.props.hasCorruptStops);
            if(this.props.hasCorruptStops) {
                alertMessage = `<div>WARNING : Some of the stops in booking may be corrupted, please reassign.</div>`;
            } else {
                alertMessage = `<div>WARNING : Server reported an issue with this booking.  Please check the details and flag in tech_support { BOOKING_ID : ${booking.id
                    } }<br>${booking.bookingError ? booking.bookingError : null}</div>`;
            }
        }

        const { note = {} } = booking;
        const attendeesCount = parseInt(hq.attendeesCount);

        const adultsCount = parseInt(hq.adultsCount) || attendeesCount;
        const kidsCount = parseInt(hq.kidsCount) || null;
        const adultsCountBackgroundColor = adultsCount <= 2 ? 'black' : '#52a8ef';
        const kidsCountBackgroundColor = kidsCount <= 2 ? 'white' : '#52a8ef';

        const {experiencePersonalisationDetails}= booking;

        let WPStatus = null;
        const WPLogoStyle = { width: '30px', height: '30px', marginLeft: '10px', cursor:experiencePersonalisationDetails?'pointer':'auto' };
        const WPLogoFreeStyle = { width: '30px', height: '30px', marginLeft: '10px', opacity: '20%', cursor:experiencePersonalisationDetails?'pointer':'auto'  };
        const onWpLogoClick = ()=>{
            this.showPersonalisationDetailsModal(experiencePersonalisationDetails?.userResponse)
        }
        const capitalizeFirstLetter = (string) => {
            if (string) {
                return string.charAt(0).toUpperCase() + string.slice(1);
            }
            return null;
        };

        // For cancelled or pending WP
        if (booking.subscriptionStatus === 'CANCELLED' || booking.subscriptionStatus === 'PENDING') {
            WPStatus = /*#__PURE__*/ React.createElement(WPLogoCancelled, {
                style: WPLogoStyle,
                title: capitalizeFirstLetter(booking?.subscriptionTier?.toLowerCase()),
                onClick:onWpLogoClick
            });
        }
        // For free WP
        else if (booking.subscriptionStatus === 'ACTIVE' && booking.subscriptionTier === 'FREE_TIER') {
            WPStatus = /*#__PURE__*/ React.createElement(WPLogoActive, { style: WPLogoFreeStyle, title: 'Free',  onClick:onWpLogoClick });
        }
        // For paid WP and legacy
        else if (booking.subscriptionStatus === 'ACTIVE') {
            WPStatus = /*#__PURE__*/ React.createElement(WPLogoActive, {
                style: WPLogoStyle,
                title: booking?.subscriptionTier
                    ? capitalizeFirstLetter(booking?.subscriptionTier?.toLowerCase())
                    : 'Standard',
                onClick:onWpLogoClick
            });
        }
        const {personalisationDetailsModal} = this.state
        // css guideline for user-select: https://css-tricks.com/almanac/properties/u/user-select/
        return (
            <div className={styles.root}>
                {this.renderAlert(alertMessage)}
                <div className={styles.summary}>
                    {/*
                      * The HQ reference and the event date. A component booking of an occasion
                      * inherits its parent's `hq` verbatim, so this column said exactly the same
                      * thing on every row of the card — it lives on the occasion header instead,
                      * and dropping it here is what brings the component name to the left edge.
                      */}
                    {!isOccasionBooking && (
                        <div
                            className={styles.largeColumn}
                            style={{
                                cursor: 'auto',
                                userSelect: 'text',
                                MozUserSelect: 'text',
                                WebkitUserSelect: 'text',
                                msUserSelect: 'text',
                            }}
                            onClick={(e) => {
                                e.stopPropagation(); // It stops opening the expansion panel.
                            }}
                        >
                            <a href={hqLink} target="_blank" rel="noopener noreferrer">
                                <span className={styles.boldFont}>{getHqEventReference(booking)}</span>
                            </a>
                            <br />
                            {dateText}{' '}
                            {timeText}
                            <br />
                            {this.renderRowFlags(promotionAlert, hasAddOns, hasUpgrade)}
                        </div>
                    )}
                    <div
                        className={styles.largeColumn}
                        style={{
                            cursor: 'auto',
                            userSelect: 'text',
                            MozUserSelect: 'text',
                            WebkitUserSelect: 'text',
                            msUserSelect: 'text',
                        }}
                        onClick={(e) => {
                            e.stopPropagation(); // It stops opening the expansion panel.
                        }}
                    >
                        <span className={styles.productNameText}>{product.name}</span>
                        <br />
                        <span className={styles.capitalized}>{productLocation}</span>
                        {isOccasionBooking && this.renderRowFlags(promotionAlert, hasAddOns, hasUpgrade)}
                    </div>
                    <div className={styles.smallColumn}>
                        <Chip
                            label={adultsCount}
                            className={styles.attendeeCountChip}
                            style={{
                                backgroundColor: adultsCountBackgroundColor,
                                color: 'white',
                                fontSize: '12px',
                                width: '22px',
                                height: '22px'
                            }}
                        />
                        <Chip
                            label={kidsCount}
                            className={styles.attendeeCountChip}
                            style={{
                                backgroundColor: kidsCountBackgroundColor,
                                border: '#52a8ef 1px solid',
                                color: 'black',
                                fontSize: '12px',
                                display: kidsCount ?? 'none',
                                width: '22px',
                                height: '22px'
                            }}
                        />
                    </div>
                    {/* Dietaries Column */}
                    <div className={styles.smallColumn}>
                        <div>
                            {glutenFree && !isEmpty(glutenFree) ? <div>{glutenFree}</div> : ''}
                            {vegetarian && !isEmpty(vegetarian) ? <div>{vegetarian}</div> : ''}
                            {vegan && !isEmpty(vegan) ? <div>{vegan}</div> : ''}
                        </div>
                    </div>
                    <div className={styles.largeColumn}>{requirementNotes.length > 0 ? requirementNotes : ''}</div>
                    {/* Inclusion Column */}
                    <div className={styles.largeColumn}>
                        {addOns ? <div className={styles.capitalized}>{addOns}</div> : ''}
                    </div>
                    {has(note, 'private') && (
                        <div className={styles.largeColumn}>
                            <div>{note.private}</div>
                            {note.dateTime && (
                                <div style={{ fontSize: '0.7rem', color: '#787878' }}>
                                    {moment(note.dateTime).format('LL')}
                                </div>
                            )}
                        </div>
                    )}
                    <div
                        className={styles.largeColumn}
                        style={{ marginRight: 20 }}
                        onClick={(e) => this.onClickBookingManager(e)}
                    >
                        <div className={styles.selectContainer}>{this.renderUsers()}</div>
                    </div>
                    {/*
                      * The customer, and the WP subscription mark next to them. Both belong to the
                      * booking HQ holds, which for an occasion is the occasion: a component
                      * inherits `hq.customer` verbatim, and neither `subscriptionStatus` nor
                      * `experiencePersonalisationDetails` is ever written to a component booking
                      * (ac-experience only sets them on the root document), so on a component row
                      * this column was the same name and phone repeated N times over an always-null
                      * WP mark. The occasion header carries the customer.
                      */}
                    {!isOccasionBooking && (
                        <div
                            className={styles.largeColumn}
                            style={{
                                cursor: 'auto',
                                userSelect: 'text',
                                MozUserSelect: 'text',
                                WebkitUserSelect: 'text',
                                msUserSelect: 'text',
                            }}
                            onClick={(e) => {
                                e.stopPropagation(); // It stops opening the expansion panel.
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'start', flexDirection: 'row' }}>
                                <div>
                                    <span className={styles.boldFont}>{customer.name}</span>
                                    <br />
                                    {phoneNumber}
                                </div>
                                <div style={{ position: 'relative', marginLeft: 'auto' ,marginRight:'10px'}}>
                                    {WPStatus}
                                    {(WPStatus && !isEmpty(experiencePersonalisationDetails?.userResponse)) && (
                                        <div style={{
                                            position: "absolute",
                                            top: "-5px",
                                            right: "-8px"
                                        }}>
                                            <span style={{
                                                color: "grey",
                                                fontSize: "14px",
                                            }}><ArrowRightIcon/></span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {booking.isPersonalisationDone?<span style={{color:'#66CDAA',padding:'5px'}}><DoneRoundedIcon/></span>:''}
                    <div className={styles.mediumColumn}>
                        {this.renderStatusButton(booking)}
                        {this.renderDeliveryStatus()}
                    </div>
                    <div className={styles.extraLargeColumn}>
                        {this.renderNotificationIcons(emailStatus, codeStatus, routeId)}
                    </div>
                </div>
                <Modal open={personalisationDetailsModal.visible} onClose={this.onClosePersonalisationDetailsModal}>
                    <div className={styles.modalRoot}>
                        <div>
                            <div className={styles.fullWidthColumn}>
                                <div className={styles.top}>
                                    <div>
                                        <h5 style={{fontWeight: 400}} >Experience Personalisation Details</h5>
                                    </div>
                                    <div>
                                        <CloseIcon className={styles.pointerCursor} onClick={this.onClosePersonalisationDetailsModal} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            {this.renderPersonalisationDetails(personalisationDetailsModal.userResponse)}
                        </div>
                    </div>
                </Modal>
            </div>
        );
    }
}
